import { Component, OnInit } from '@angular/core';
import { ShipService } from '../../../../services/ship.service';
import { Ship } from '../../../../Ship';
import { MarineBulkEnrollee } from '../../../../MarineBulkEnrollee';
import { EntitlementService } from '../../../../services/entitlement.service';

@Component({
  selector: 'app-marines-bulk-enrollment',
  templateUrl: './marines-bulk-enrollment.component.html',
  styleUrl: './marines-bulk-enrollment.component.css'
})
export class MarinesBulkEnrollmentComponent implements OnInit {

  constructor(
    private shipService: ShipService,
    private entitlementService: EntitlementService
  ) { }

  EMAIL_PATTERN: RegExp = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  PHONE_PATTERN: RegExp = /^[0-9\-]{9,25}$/;
  DOB_PATTERN: RegExp = /^[0-9]{8}$/;
  SSN_PATTERN: RegExp = /^[0-9]{9}$/;
  TEXT_1_PATTERN: RegExp = /^[a-zA-Z]{1}$/;
  TEXT_2_PATTERN: RegExp = /^[a-zA-Z]{2}$/;
  ABA_PATTERN: RegExp = /^[0-9]{9}$/;
  BANK_ACCT_PATTERN: RegExp = /^[0-9\-]{2,17}$/;
  TEXT_10MAX_PATTERN: RegExp = /^[a-zA-Z]{2,10}$/;
  NMBR_10MAX_PATTERN: RegExp = /^[0-9]{2,10}$/;
  TEXT_12MAX_PATTERN: RegExp = /^[a-zA-Z]{2,12}$/;
  TEXT_15MAX_PATTERN: RegExp = /^[a-zA-Z ]{2,15}$/;
  TEXT_22MAX_PATTERN: RegExp = /^[a-zA-Z ]{2,22}$/;
  TEXT_30MAX_PATTERN: RegExp = /^[a-zA-Z ]{2,30}$/;
  TEXT_34MAX_PATTERN: RegExp = /^[0-9a-zA-Z\.\- ]{2,34}$/;

  ship: Ship | null = null;
  shipResultArray: Ship[] = [];
  csvErrorMsg = '';
  useFile: File | null = null;
  enrolleeData: MarineBulkEnrollee[] = [];
  fileSelected = false;
  failedRecordCount = 0;
  uploadSuccess = false;
  decryptedCsv: string = '';
  decrypting = false;

  ngOnInit() {
    this.ship = this.shipService.retrieveShipProfile();
    this.shipResultArray = this.shipService.retieveAllShipProfileResults();
  }

  handleFileInput(files: FileList) {
    this.useFile = files[0];
    if (this.useFile.name.indexOf('NCBENR') <= 0 || !(this.useFile.name.endsWith('.csv') || this.useFile.name.endsWith('.enc'))) {
      this.cancel();
      this.csvErrorMsg = "The filename must include NCBENR and end with an extension of .csv or .enc";
      return;
    }
    if (this.useFile.type === 'text/csv') {
      this.processTextFirst(this.useFile);
    } else {
      this.decryptFileFirst(this.useFile);
    }
  }

  processTextFirst(file: File) {
    const fr = new FileReader();
      fr.onload = (e) => {
        this.decryptedCsv = e.target?.result as string;
        this.processCsv();
    }

    fr.readAsText(file);
  }

  decryptFileFirst(file: File){
    this.decrypting = true;
    this.shipService.decryptCsv(file).subscribe((resp) => {
      this.decryptedCsv = resp;
      this.processCsv();
      this.decrypting = false;
    },
     (err) => {
        console.error(err);
        this.cancel();
        this.csvErrorMsg = 'There was a problem uploading the enrollment data.';
        this.decrypting = false;
      });
  }

  processCsv() {
    const jsonData = this.csvToJson(this.decryptedCsv);
    jsonData.forEach(mbe => {
      this.enrolleeData.push(mbe as MarineBulkEnrollee);
    });
    this.validateData(this.enrolleeData);
  }

  csvToJson(csv: string): any[] {
    const lines = csv.split('\n').filter(line => line.trim());
    // We provide column headers - the slicing begins at 1 since we ignore the provided header info
    const colHeaders = 'recordType,lastName,firstName,middleName,ssn,dob,mothersMaiden,salutation,payGrade,militaryBranch,addressLine1,addressLine2,addressLine3,city,state,zip,country,phone,email,bankAba,bankAccount,bankAccountName,accountType,cardSeq,meu,enrollmentFormName';
    const headers = colHeaders.split(',').map(h => h.trim());

    return lines.slice(1).map(line => {
      const values = line.split(',').map(v => v.trim());
      // The value 3 means it is the trailer row
      if (values[0] !== '3') {
        return headers.reduce((obj, header, index) => {
          obj[header] = values[index];
          return obj as MarineBulkEnrollee;
        }, {} as any);
      }
    });
  }

  validateData(dataToValidate: MarineBulkEnrollee[]): void {
    dataToValidate.forEach((enrollee) => {
      // One false marks whole record as 0 making its valid state false (0)
      if (enrollee) {
        let checkValid = 1;
        checkValid = checkValid * this.isValid(enrollee.lastName, true, this.TEXT_30MAX_PATTERN);
        checkValid = checkValid * this.isValid(enrollee.firstName, true, this.TEXT_30MAX_PATTERN);
        checkValid = checkValid * this.isValid(enrollee.middleName, false, /^[a-zA-Z]{1,30}$/);
        checkValid = checkValid * this.isValid(enrollee.ssn, true, this.SSN_PATTERN);
        checkValid = checkValid * this.isValid(enrollee.dob, true, this.DOB_PATTERN);
        checkValid = checkValid * this.isValid(enrollee.mothersMaiden, true, this.TEXT_30MAX_PATTERN);
        checkValid = checkValid * this.isValid(enrollee.salutation, false, this.TEXT_10MAX_PATTERN);
        checkValid = checkValid * this.isValid(enrollee.payGrade, true, /^[0-9a-zA-Z]{2,10}$/);
        checkValid = checkValid * this.isValid(enrollee.militaryBranch, true, this.TEXT_12MAX_PATTERN);
        checkValid = checkValid * this.isValid(enrollee.addressLine1, true, this.TEXT_34MAX_PATTERN);
        checkValid = checkValid * this.isValid(enrollee.addressLine2, false, this.TEXT_34MAX_PATTERN);
        checkValid = checkValid * this.isValid(enrollee.addressLine3, false, this.TEXT_34MAX_PATTERN);
        checkValid = checkValid * this.isValid(enrollee.city, true, /^[a-zA-Z ]{2,34}$/);
        checkValid = checkValid * this.isValid(enrollee.state, true, this.TEXT_2_PATTERN);
        checkValid = checkValid * this.isValid(enrollee.zip, true, /^[0-9a-zA-Z\-]{5,10}$/);
        checkValid = checkValid * this.isValid(enrollee.country, true, this.TEXT_15MAX_PATTERN);
        checkValid = checkValid * this.isValid(enrollee.phone, true, this.PHONE_PATTERN);
        checkValid = checkValid * this.isValid(enrollee.email, true, this.EMAIL_PATTERN);
        checkValid = checkValid * this.isValid(enrollee.bankAba, true, this.ABA_PATTERN);
        checkValid = checkValid * this.isValid(enrollee.bankAccount, true, this.BANK_ACCT_PATTERN);
        checkValid = checkValid * this.isValid(enrollee.bankAccountName, true, this.TEXT_22MAX_PATTERN);
        checkValid = checkValid * this.isValid(enrollee.accountType, true, this.TEXT_1_PATTERN);
        checkValid = checkValid * this.isValid(enrollee.meu, true, /^([1][1]|[2][2]|[3][1])$/);

        enrollee.valid = checkValid > 0;
        if (!enrollee.valid) {
          this.failedRecordCount++;
        }
      }
      this.fileSelected = true;
    });
  }

  isNotEmpty(val: string | null | undefined): boolean {
    return val !== undefined && val !== null && val.trim().length > 0;
  }

  isValid(val: string | null | undefined, required: boolean, regx: RegExp): number {
    // If not required, return true on empty, otherwise need to follow other rules
    if (!required && !this.isNotEmpty(val)) {
      return 1;
    }

    return this.isNotEmpty(val) && regx.test(val.trim()) ? 1 : 0;
  }

  cancel() {
    this.enrolleeData = [];
    this.fileSelected = false;
    this.failedRecordCount = 0;
    this.uploadSuccess = false;
    this.csvErrorMsg = '';
    this.decryptedCsv = '';
  }

  upload() {
    if (this.ship !== null && this.useFile !== null) {
      let useFileName = this.useFile.name;
      if (useFileName.endsWith('.enc')) {
        useFileName = useFileName.substring(0, useFileName.lastIndexOf('.enc'));
        if (!useFileName.endsWith('.csv')) {
          useFileName = useFileName + '.csv';
        }
      }
      this.shipService.uploadCsv(this.ship.shipLocation, this.decryptedCsv, useFileName).subscribe((resp) => {
        this.cancel();
        this.uploadSuccess = true;
      }, (err) => {
        console.error(err);
        this.cancel();
        this.csvErrorMsg = 'There was a problem uploading the enrollment data.';
      });
    }
  }

  hasEntitlement(entitlement: string) {
    return this.entitlementService.hasEntitlement(entitlement);
  }

  downloadData() {
    if (this.decryptedCsv != '' && this.useFile != null) {
      const blob = new Blob([this.decryptedCsv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      let filename = this.useFile.name;
      filename = filename.substring(0, filename.lastIndexOf('.'));
      if (!filename.endsWith('.csv')) {
        filename += '.csv';
      }
      link.download = filename;
      link.click();

      URL.revokeObjectURL(url);
    }
  }

  isNotEMV() {
    return this.ship !== null && this.ship.isEMV === '0';
  }

}
